import { useRef, useState } from "react";
import Card from "@components/ui/Card";
import { createT, type LocaleDict } from "@/lib/i18n";
import { KINDS, SOURCE, VERSION, example, validate, type Kind, type Result } from "../../lib/acp/validate";

export default function AcpValidator({ i18n }: { i18n: LocaleDict }) {
  const t = createT(i18n);
  const tr = (key: string) => t(`acp_validator.${key}`);
  const [kind, setKind] = useState<Kind>("create");
  const [input, setInput] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [checked, setChecked] = useState("");
  const [notice, setNotice] = useState("");
  const resultRef = useRef<HTMLHeadingElement>(null);
  const generation = useRef(0);
  const stale = checked !== `${kind}\n${input}`;
  const button = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline-indigo-600";
  const changeInput = (value: string) => { generation.current++; setInput(value); setNotice(""); };
  const load = (invalid: boolean) => {
    if (input.trim() && !window.confirm(tr("replace"))) return;
    changeInput(example(kind, invalid)); setResult(null);
  };
  const ruleText = (rule: string) => tr(`rules.${["required", "type", "additionalProperties", "format", "enum"].includes(rule) ? rule : "other"}`);
  const copy = async () => {
    if (!result || stale) return;
    const text = [VERSION, tr(`kinds.${kind}`), ...result.issues.map((issue) => `${issue.path}: ${ruleText(issue.rule)} (${issue.detail})`)].join("\n");
    try { await navigator.clipboard.writeText(text); setNotice(tr("copied")); }
    catch { setNotice(tr("copy_failed")); }
  };
  return (
    <div className="rr-block" data-private="true">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div><label htmlFor="acp-kind" className="mb-2 block text-sm font-semibold">{tr("kind")}</label>
          <select id="acp-kind" value={kind} onChange={(e) => { generation.current++; setKind(e.target.value as Kind); setNotice(""); }} className="max-w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5">
            {KINDS.map((k) => <option key={k} value={k}>{tr(`kinds.${k}`)}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2"><button className={button} onClick={() => load(false)}>{tr("sample")}</button><button className={button} onClick={() => load(true)}>{tr("bad_sample")}</button></div>
      </div>
      <p className="mb-5 text-sm leading-6 text-slate-600">{tr(`directions.${kind}`)}</p>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card className="min-w-0">
          <label htmlFor="acp-input" className="mb-3 block font-semibold">{tr("input")}</label>
          <textarea id="acp-input" value={input} onChange={(e) => changeInput(e.target.value)} spellCheck={false} autoComplete="off" autoCorrect="off" autoCapitalize="off"
            placeholder={tr("placeholder")} className="h-80 w-full resize-y rounded-lg border border-slate-300 bg-slate-950 p-4 font-mono text-sm leading-6 text-slate-100 focus:outline-indigo-500" />
          <label className="mt-3 block text-sm font-medium" htmlFor="acp-file">{tr("file")}</label>
          <input id="acp-file" className="mt-2 block w-full text-sm" type="file" accept=".json,application/json" onChange={async (event) => {
            const file = event.target.files?.[0]; if (!file) return;
            const token = ++generation.current;
            if (file.size > 1_000_000) { setNotice(tr("limit")); event.target.value = ""; return; }
            try { const text = await file.text(); if (token === generation.current) { setInput(text); setNotice(""); } }
            catch { if (token === generation.current) setNotice(tr("failed")); }
            event.target.value = "";
          }} />
          <div className="mt-4 flex flex-wrap gap-2">
            <button className={button} onClick={() => { try { changeInput(JSON.stringify(JSON.parse(input), null, 2)); } catch { setNotice(tr("syntax")); } }}>{tr("format")}</button>
            <button className={button} onClick={() => { changeInput(""); setResult(null); }}>{tr("clear")}</button>
            <button disabled={!input.trim()} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40" onClick={() => {
              setResult(validate(input, kind)); setChecked(`${kind}\n${input}`); setNotice(""); requestAnimationFrame(() => resultRef.current?.focus());
            }}>{tr("run")}</button>
          </div>
          <p className="mt-3 text-xs leading-5 text-slate-500">{tr("sample_note")}</p>
        </Card>
        <Card className="min-w-0" aria-live="polite">
          <h2 ref={resultRef} tabIndex={-1} className="text-lg font-semibold outline-none">{tr("result")}</h2>
          {!result ? <p className="mt-4 leading-7 text-slate-500">{tr("idle")}</p> : stale ? <p className="mt-4 text-amber-800">{tr("stale")}</p> : <>
            <p className={`mt-4 rounded-lg p-4 font-semibold ${result.status === "valid" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>{tr(result.status)}</p>
            {result.status === "valid" && <p className="mt-4 text-sm leading-7 text-slate-600">{tr("next")}</p>}
            <ul className="mt-4 max-h-[32rem] space-y-4 overflow-y-auto">
              {result.issues.map((issue, index) => <li key={index} className="break-words border-b border-slate-100 pb-4 text-sm leading-6">
                <p className="font-semibold">{ruleText(issue.rule)}</p>
                <p>{tr("path")}: <code>{issue.path}</code></p>
                {issue.detail && <p>{tr("detail")}: <code>{issue.detail}</code></p>}
              </li>)}
            </ul>
            {result.truncated && <p className="mt-3 text-sm">{tr("truncated")}</p>}
            {result.issues.length > 0 && <button className={`${button} mt-4`} onClick={() => void copy()}>{tr("copy")}</button>}
          </>}
          <a className="mt-5 inline-block text-sm font-semibold text-indigo-600 underline" href={SOURCE} target="_blank" rel="noreferrer">{tr("source")}</a>
        </Card>
      </div>
      {notice && <p role="status" className="mt-4 text-sm text-amber-900">{notice}</p>}
    </div>
  );
}
